import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

import { EntityTypeEnum } from '../../enums/entity-type.enum';
import { RepertoireFiltersEnum } from '../../enums/repertoire-filters.enum';
import { IEnum } from '../../interfaces/enum.interface';
import { IClass } from '../../interfaces/groups/class.interface';
import { IGroup } from '../../interfaces/groups/group.interface';
import { ISchool } from '../../interfaces/groups/school.interface';
import { IRepertoireEntry, IRepertoireFilter, IRepertoireLetter } from '../../interfaces/repertoire.interface';
import { ILightUser, IUser, IUsersLight } from '../../interfaces/users/user.interface';
import { alphabetLetters } from '../../utils/alphabet-letter.utils';
import { Latinize } from '../../utils/latinize.utils';
import { ResetRepertoire, SetRepertoire, SetRepertoireFilter, SetRepertoireSearch } from '../actions/repertoire.action';
import { SetCurrentScrollLetter } from './../actions/repertoire.action';
import { AuthState } from './auth.state';
import { ClassesState } from './classes.state';
import { EnumsState } from './enums.state';
import { GroupsState } from './groups.state';
import { SchoolsState } from './schools.state';
import { UsersState } from './users.state';

export interface RepertoireStateModel {
    repertoire: IRepertoireLetter[];
    filteredRepertoire: IRepertoireLetter[];
    filters: IRepertoireFilter[];
    searchValue: string;
    currentScrollLetter: string;
}

@State<RepertoireStateModel>({
    name: 'repertoire',
    defaults: {
        repertoire: [],
        filteredRepertoire: [],
        filters: [],
        searchValue: '',
        currentScrollLetter: null,
    },
})
@Injectable()
export class RepertoireState {
    constructor(private store: Store) {}
    @Selector()
    public static getRepertoire(state: RepertoireStateModel): IRepertoireLetter[] {
        return state.repertoire.filter((item: IRepertoireLetter) => item.data.length);
    }

    @Selector()
    public static getFilteredRepertoire(state: RepertoireStateModel): IRepertoireLetter[] {
        return state.filteredRepertoire;
    }

    @Selector()
    public static getRepertoireSearchValue(state: RepertoireStateModel): string {
        return state.searchValue;
    }

    @Selector()
    public static getRepertoireFilters(state: RepertoireStateModel): IRepertoireFilter[] {
        return state.filters;
    }

    @Selector()
    public static getCurrentScrollLetter(state: RepertoireStateModel): string {
        return state.currentScrollLetter;
    }

    @Action(SetRepertoire)
    public setRepertoire(ctx: StateContext<RepertoireStateModel>): void {
        const users: IUsersLight = this.store.selectSnapshot(UsersState.getAllUsersLight);
        const groups: IGroup[] = this.store.selectSnapshot(GroupsState.getAllGroups);
        const classes: IClass[] = this.store.selectSnapshot(ClassesState.getCurrentYearClasses);
        const schools: ISchool[] = this.store.selectSnapshot(SchoolsState.getAllSchools);
        const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
        const isEmployee: boolean = connectedUser.type === EntityTypeEnum.EMPLOYEE || connectedUser.type === EntityTypeEnum.ADMINISTRATOR;
        let result: IRepertoireLetter[] = [];

        if (users && groups && classes) {
            const repertoireAdmins: IRepertoireEntry[] = users.administrators
                .map((user: ILightUser) => ({
                    id: user.id,
                    name: (user.firstname + ' ' + user.lastname).includes(null) ? user.email : user.firstname + ' ' + user.lastname,
                    latinNames: Latinize.latinize(
                        (user.lastname + ' ' + user.firstname).includes(null) ? user.email : user.firstname + ' ' + user.lastname
                    ),
                    searchNames: Latinize.latinize(
                        (user.lastname + ' ' + user.firstname).includes(null)
                            ? user.email
                            : user.lastname + ' ' + user.firstname + ' ' + user.lastname
                    ),
                    type: user.type,
                    entity: user,
                    roles: user.role,
                }))
                .filter((user: IRepertoireEntry) => user.id !== currentUser.id);

            const repertoireEmployees: IRepertoireEntry[] = users.employees
                .map((user: ILightUser) => ({
                    id: user.id,
                    name:
                        isEmployee ? user.displayName : user.firstname + ' ' + user.lastname,
                    latinNames: Latinize.latinize(
                        isEmployee ? user.displayName : user.firstname + ' ' + user.lastname
                    ),
                    searchNames: Latinize.latinize(
                        isEmployee ? user.displayName : user.firstname + ' ' + user.lastname
                    ),
                    type: user.type,
                    entity: user,
                    roles: user.role,
                }))
                .filter((user: IRepertoireEntry) => user.id !== currentUser.id);

            let repertoireParents: IRepertoireEntry[] = [];
            repertoireParents = users.parents
                .map((user: ILightUser) => ({
                    id: user.id,
                    name: user.firstname + ' ' + user.lastname,
                    latinNames: Latinize.latinize(user.firstname + ' ' + user.lastname),
                    searchNames: Latinize.latinize(user.lastname + ' ' + user.firstname + ' ' + user.lastname),
                    type: user.type,
                    entity: user,
                    roles: user.role,
                }))
                .filter((user: IRepertoireEntry) => user.id !== currentUser.id && user.id !== connectedUser.id);

            const repertoireStudents: IRepertoireEntry[] = users.students
                .map((user: ILightUser) => ({
                    id: user.id,
                    name: user.firstname + ' ' + user.lastname,
                    latinNames: Latinize.latinize(user.firstname + ' ' + user.lastname),
                    searchNames: Latinize.latinize(user.lastname + ' ' + user.firstname + ' ' + user.lastname),
                    type: user.type,
                    entity: user,
                    roles: user.role,
                }))
                .filter((user: IRepertoireEntry) => user.id !== currentUser.id);

            let repertoireRelatives: IRepertoireEntry[] = [];
            if (currentUser.type === EntityTypeEnum.PARENT || currentUser.type === EntityTypeEnum.STUDENT) {
                repertoireRelatives = users.relatives
                    .map((user: ILightUser) => ({
                        id: user.id,
                        name: user.firstname + ' ' + user.lastname,
                        latinNames: Latinize.latinize(user.firstname + ' ' + user.lastname),
                        searchNames: Latinize.latinize(user.lastname + ' ' + user.firstname + ' ' + user.lastname),
                        type: user.type,
                        entity: user,
                        roles: user.role,
                    }))
                    .filter((user: IRepertoireEntry) => user.id !== currentUser.id);
            }

            const repertoireContacts: IRepertoireEntry[] =
                users.contacts
                    .map((user: ILightUser) => ({
                        id: user.id,
                        name: user?.firstname ? user.firstname + ' ' + user.lastname : user.name,
                        latinNames: Latinize.latinize(user?.firstname ? user.firstname + ' ' + user.lastname : user.name),
                        searchNames: Latinize.latinize(
                            user?.firstname ? user.lastname + ' ' + user.firstname + ' ' + user.lastname : user.name
                        ),
                        type: user.type,
                        entity: user,
                        roles: user.role,
                    }))
                    .filter((user: IRepertoireEntry) => user.id !== currentUser.id) || [];

            const repertoireGroups: IRepertoireEntry[] = groups
                .filter(
                    (group: IGroup) =>
                        // les employés ont accès au groupe avec le meme educ level
                        // les parents et student on pas accès aux groupes prédéfinis
                        // tous peuvent voir les groupes non-auto-générés dans lesquels ils sont membres
                        (currentUser.type === EntityTypeEnum.EMPLOYEE &&
                            (!group.educationLevel ||
                                group.educationLevel.some((educationLevel: IEnum) =>
                                    currentUser.educationLevel.some((uEducationLevel: IEnum) => educationLevel.id === uEducationLevel.id)
                                ))) ||
                        (currentUser.type === EntityTypeEnum.EMPLOYEE && group.autoGenerated) ||
                        (currentUser.type !== EntityTypeEnum.PARENT &&
                            currentUser.type !== EntityTypeEnum.STUDENT &&
                            group.autoGenerated) ||
                        (!group.autoGenerated && group.memberIds.some((id: number) => id === currentUser.id))
                )
                .map((group: IGroup) => ({
                    id: group.id,
                    name: group.name,
                    latinNames: Latinize.latinize(group.name),
                    searchNames: Latinize.latinize(group.name),
                    type: group.autoGenerated ? EntityTypeEnum.GROUP_PREDEFINED : EntityTypeEnum.GROUP_PERSO,
                    entity: group,
                }));

            let repertoireClasses: IRepertoireEntry[] = [];
            if (currentUser.id === connectedUser.id) {
                repertoireClasses = classes.map((schoolClass: IClass) => ({
                    id: schoolClass.id,
                    name: schoolClass.name,
                    latinNames: Latinize.latinize(schoolClass.name),
                    searchNames: Latinize.latinize(schoolClass.name),
                    type: schoolClass.type,
                    entity: schoolClass,
                }));
            }

            let repertoireSchools: IRepertoireEntry[] = [];
            if (currentUser?.type === EntityTypeEnum.ADMINISTRATOR) {
                repertoireSchools = schools.map((school: ISchool) => ({
                    id: school.id,
                    name: school.name,
                    latinNames: Latinize.latinize(school.name),
                    searchNames: Latinize.latinize(school.name),
                    type: school.type,
                    entity: school,
                }));
            }

            const merged: IRepertoireEntry[] = [
                ...repertoireAdmins,
                ...repertoireEmployees,
                ...repertoireParents,
                ...repertoireStudents,
                ...repertoireRelatives,
                ...repertoireContacts,
                ...repertoireGroups,
                ...repertoireClasses,
                ...repertoireSchools,
            ].sort((a: IRepertoireEntry, b: IRepertoireEntry) =>
                a.latinNames.localeCompare(b.latinNames, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                })
            );
            alphabetLetters.forEach((letter: string) => {
                const repertoireLetter: IRepertoireLetter = {
                    letter,
                    data: merged.filter((value: IRepertoireEntry) =>
                        letter === '#'
                            ? ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(value.latinNames.toLowerCase().charAt(0))
                            : value.latinNames.toLowerCase().charAt(0) === letter
                    ),
                };
                result = [...result, repertoireLetter];
            });
            ctx.patchState({
                repertoire: result,
                filteredRepertoire: result,
            });
            this.filterAndSearchRepertoire(ctx);
        }
    }

    @Action(ResetRepertoire)
    public resetRepertoire(ctx: StateContext<RepertoireStateModel>): void {
        ctx.patchState({
            repertoire: [],
            filteredRepertoire: [],
            filters: [],
            searchValue: '',
            currentScrollLetter: '',
        });
    }

    @Action(SetRepertoireSearch)
    public repertoireSearch(ctx: StateContext<RepertoireStateModel>, action: SetRepertoireSearch): IRepertoireLetter[] {
        ctx.patchState({
            searchValue: action.searchValue.length >= 2 ? action.searchValue : '',
        });
        return this.filterAndSearchRepertoire(ctx);
    }

    @Action(SetRepertoireFilter)
    public repertoireFilter(ctx: StateContext<RepertoireStateModel>, action: SetRepertoireFilter): IRepertoireLetter[] {
        ctx.patchState({
            filters: action.filters,
        });
        return this.filterAndSearchRepertoire(ctx);
    }

    @Action(SetCurrentScrollLetter)
    public setCurrentScrollLetter(ctx: StateContext<RepertoireStateModel>, action: SetCurrentScrollLetter): void {
        ctx.patchState({
            currentScrollLetter: action.letter,
        });
    }

    private filterAndSearchRepertoire(ctx: StateContext<RepertoireStateModel>): IRepertoireLetter[] {
        const state: RepertoireStateModel = ctx.getState();
        const filters: IRepertoireFilter[] = state.filters;
        const searchValue: string = state.searchValue;
        let filteredRepertoire: IRepertoireLetter[] = state.repertoire;
        const educationLevelsEnum: IEnum[] = this.store.selectSnapshot(EnumsState.getEducationLevelsEnum);

        if (filters.length) {
            filters.forEach((filter: IRepertoireFilter) => {
                if (filter.filterType === RepertoireFiltersEnum.SCHOOL_YEAR) {
                    const filterValue: number = filter.filterValues;
                    filteredRepertoire = filteredRepertoire.map((value: IRepertoireLetter) => ({
                        letter: value.letter,
                        data: value.data.filter((entry: IRepertoireEntry) => this.schoolYearFilter(entry, filterValue)),
                    }));
                }

                if (
                    filter.filterType === RepertoireFiltersEnum.EDUCATION_LEVEL &&
                    filter.filterValues?.length !== educationLevelsEnum.length
                ) {
                    const filterValues: number[] = filter.filterValues;
                    filteredRepertoire = filteredRepertoire.map((value: IRepertoireLetter) => ({
                        letter: value.letter,
                        data: value.data.filter((entry: IRepertoireEntry) => {
                            // Disable education level filter for admins & relatives
                            if ([EntityTypeEnum.ADMINISTRATOR, EntityTypeEnum.RELATIVE].includes(entry.type)) {
                                return entry;
                            } else {
                                return this.educationLevelFilter(entry, filterValues);
                            }
                        }),
                    }));
                }

                if (filter.filterType === RepertoireFiltersEnum.ENTITY_TYPES) {
                    const filterValues: string[] = filter.filterValues;
                    filteredRepertoire = filteredRepertoire.map((value: IRepertoireLetter) => ({
                        letter: value.letter,
                        data: value.data.filter((entry: IRepertoireEntry) => this.entityTypeFilter(entry, filterValues)),
                    }));
                }
            });
        }
        if (searchValue) {
            filteredRepertoire = filteredRepertoire.map((value: IRepertoireLetter) => ({
                letter: value.letter,
                data: value.data.filter((entry: IRepertoireEntry) =>
                    entry.searchNames.toLowerCase().includes(Latinize.latinize(searchValue).trim().toLowerCase())
                ),
            }));
        }
        ctx.patchState({
            filteredRepertoire,
        });
        return filteredRepertoire;
    }

    private schoolYearFilter(entry: IRepertoireEntry, idToFind: number): boolean {
        return (
            (entry.type === EntityTypeEnum.GROUP_PERSO || entry.type === EntityTypeEnum.GROUP_PREDEFINED) &&
            ((entry.entity as IGroup).schoolYear.id === idToFind) !== undefined
        );
    }

    private educationLevelFilter(entry: IRepertoireEntry, filterValues: number[]): boolean {
        return entry.entity?.educationLevel?.find((edLevel: IEnum) => filterValues.includes(edLevel.id)) !== undefined;
    }

    private entityTypeFilter(entry: IRepertoireEntry, filterValues: string[]): boolean {
        return filterValues.includes(entry.type.toUpperCase());
    }
}
